import type { ComponentRuntimeDefinition } from '../../catalog/index.ts';
import { serializeZodSchema } from '../../catalog/ProtocolSchemaRegistry.ts';
import {
  jsonObjectSchema,
  sceneGrammarSchema,
} from '../../contracts/index.ts';
import { createBuiltinSceneCapabilityRegistry } from '../../scene/index.ts';
import { componentHardMinimum } from '../shared/sizingProfiles.ts';

const defaultScene = sceneGrammarSchema.parse({
  schemaVersion: '1.0.0',
  id: 'scene-gallery',
  title: '二维场景能力示例',
  intent: {
    knowledge: '观察对象经过一个过程从初始状态迁移到目标状态。',
    metaphor: '对象沿带方向的路径移动。',
    focus: ['对象', '过程', '目标'],
    learningGoals: ['识别过程中的状态与方向'],
  },
  viewport: {
    width: 800,
    height: 450,
    background: '#f8fbff',
    cameras: [{
      id: 'overview',
      mode: 'fit-scene',
      padding: 48,
      interactive: { pan: false, zoom: false },
    }],
    activeCameraId: 'overview',
  },
  nodes: [
    {
      id: 'scene-root',
      kind: 'group',
      semantic: {
        label: '过程场景',
        role: 'scene',
      },
    },
    {
      id: 'source',
      parentId: 'scene-root',
      kind: 'shape',
      transform: { x: 110, y: 170 },
      geometry: { type: 'rect', width: 150, height: 100, radius: 10 },
      style: {
        fill: '#dbeafe',
        stroke: '$primary',
        strokeWidth: 2,
      },
      semantic: { label: '初始区域', role: 'source' },
      accessibility: { label: '初始区域' },
      interactionIds: ['select-object'],
    },
    {
      id: 'source-label',
      parentId: 'scene-root',
      kind: 'text',
      text: '初始状态',
      transform: { x: 185, y: 220 },
      textStyle: {
        color: '$text',
        fontSize: 18,
        fontWeight: '600',
        align: 'center',
      },
      anchorX: 0.5,
      anchorY: 0.5,
    },
    {
      id: 'target',
      parentId: 'scene-root',
      kind: 'shape',
      transform: { x: 540, y: 170 },
      geometry: { type: 'rect', width: 150, height: 100, radius: 10 },
      style: {
        fill: '#ecfeff',
        stroke: '$secondary',
        strokeWidth: 2,
      },
      semantic: { label: '目标区域', role: 'target' },
      accessibility: { label: '目标区域' },
      interactionIds: ['select-object'],
    },
    {
      id: 'target-label',
      parentId: 'scene-root',
      kind: 'text',
      text: '目标状态',
      transform: { x: 615, y: 220 },
      textStyle: {
        color: '$text',
        fontSize: 18,
        fontWeight: '600',
        align: 'center',
      },
      anchorX: 0.5,
      anchorY: 0.5,
    },
    {
      id: 'moving-object',
      parentId: 'scene-root',
      kind: 'shape',
      transform: { x: 330, y: 220 },
      geometry: { type: 'circle', radius: 22 },
      style: {
        fill: '$primary',
        stroke: '#ffffff',
        strokeWidth: 3,
      },
      stateStyles: {
        selected: { fill: '$selected', strokeWidth: 4 },
      },
      semantic: { label: '过程对象', role: 'entity' },
      accessibility: { label: '过程对象', focusable: true },
      interactionIds: ['select-object', 'play-motion'],
    },
  ],
  relations: [{
    id: 'process-path',
    type: 'connect',
    sourceId: 'source',
    targetId: 'target',
    sourceAnchor: 'east',
    targetAnchor: 'west',
    route: 'straight',
    markerEnd: 'arrow',
    label: '状态迁移',
    style: { stroke: '$primary', strokeWidth: 3 },
  }],
  states: [{
    id: 'selected',
    source: 'selected',
    priority: 100,
    style: { fill: '$selected', strokeWidth: 4 },
  }],
  timelines: [{
    id: 'object-motion',
    root: {
      id: 'motion-sequence',
      type: 'sequence',
      steps: [
        {
          id: 'move-right',
          type: 'tween',
          targetIds: ['moving-object'],
          property: 'transform.x',
          from: 330,
          to: 470,
          durationMs: 700,
          easing: 'ease-in-out',
        },
        {
          id: 'motion-checkpoint',
          type: 'checkpoint',
          name: '对象到达目标前',
        },
      ],
    },
  }],
  interactions: [
    {
      id: 'select-object',
      event: 'pointertap',
      actions: [{ type: 'selection', mode: 'toggle' }],
    },
    {
      id: 'play-motion',
      event: 'pointertap',
      actions: [{
        type: 'timeline',
        timelineId: 'object-motion',
        command: 'play',
      }],
    },
  ],
});

const capabilities = createBuiltinSceneCapabilityRegistry()
  .listDescriptors()
  .map(item => item.key);
const defaultSceneConfig = jsonObjectSchema.parse(defaultScene);

export const sceneDefinition: ComponentRuntimeDefinition = {
  descriptor: {
    key: 'probfun.scene@1',
    category: 'simulation',
    title: '通用二维教学场景',
    description: '由 SceneGrammar、Compiler、增量 Reconciler 与 PixiJS v8 驱动的通用二维场景。',
    capabilities,
    suitableFor: [
      '随机运动',
      '空间分布',
      '流程与状态',
      '网络连接',
      '抽样与实验',
      '随机过程',
      '教学叙事',
    ],
    inputs: [{
      name: 'sceneData',
      description: 'Scene DataView 可引用的一个或多个 ObservationDataset',
      required: false,
      multiple: true,
      acceptedDatasetKinds: [
        'scalar',
        'spatial',
        'table',
        'series',
        'event-series',
        'graph',
        'distribution',
      ],
    }],
    outputs: [],
    events: [
      {
        type: 'selection.changed',
        description: '场景节点或 Dataset 实体选择变化',
      },
      {
        type: 'timeline.checkpoint',
        description: '声明式教学时间线到达检查点',
      },
      {
        type: 'timeline.triggered',
        description: '声明式时间线发出受控触发事件',
      },
      {
        type: 'scene.diagnostics',
        description: '编译和 Pixi Runtime 返回结构化场景诊断',
      },
    ],
    constraints: [
      '只接受 sceneGrammarSchema 允许的 JSON 配置',
      '不执行 HTML、SVG 文档、JavaScript、函数或 PixiJS 代码',
      '动态内容只能通过 ObservationDataset binding 和 Scene DataView 驱动',
      '图片只能引用 ArtifactManifest 中已就绪的安全资源',
    ],
    configSchemaId: 'probfun.scene-grammar@1',
    configSchema: serializeZodSchema(
      sceneGrammarSchema,
      'probfun.scene-grammar@1',
    ),
    defaultConfig: defaultSceneConfig,
    examples: [defaultSceneConfig],
    notes: [
      'VisualConstruct 在 Compiler 阶段展开为基础 SceneGrammar。',
      '组件内部只消费 NormalizedSceneIR；PixiJS 不进入 AI 契约。',
      '长文本、公式和复杂说明应使用独立 Block，而非场景 DOM Overlay。',
      '外框按本份 SceneGrammar 的 viewport 宽高比作为显示区；默认 contain 进格子且不拉伸。拖动改尺寸只用 CSS 缩放，不重设 WebGL。',
      '学生不在场景里滚轮缩放或拖动画布；教学操作交给模块外的 action-button。',
    ],
    layout: {
      ...componentHardMinimum('probfun.scene@1'),
      preferredAspectRatio: 1.7778,
      maxWidth: 1600,
      maxHeight: 900,
    },
    runtimeRequirements: [],
    metadata: {
      renderer: 'pixi.js@8',
      grammarVersion: '1.0.0',
      capabilityCount: capabilities.length,
      layoutHints: {
        roles: ['primary'],
        preferredSize: 'large',
        supportsCompact: true,
        supportsExpanded: true,
        canStack: false,
        priority: 90,
      },
    },
  },
  configSchema: sceneGrammarSchema,
  validateBlock: (block) => {
    const scene = sceneGrammarSchema.parse(block.config);
    const bindingIssues = scene.dataViews.flatMap((dataView) => {
      const binding = block.bindings.find(
        item => item.input === dataView.binding,
      );
      if (!binding) {
        return [{
          level: 'error' as const,
          path: `config.dataViews.${dataView.id}.binding`,
          message: `Scene DataView 缺少 Block binding：${dataView.binding}`,
        }];
      }
      const requiredFields = new Set([
        ...dataView.select,
        ...Object.keys(dataView.rename),
        ...dataView.filters.map(filter => filter.field),
        ...dataView.sort.map(sort => sort.field),
        ...dataView.groupBy,
        ...dataView.lookup.flatMap(lookup => [
          lookup.localField,
          ...lookup.fields,
        ]),
        ...Object.keys(dataView.format),
      ]);
      const declaredFields = new Set([
        ...binding.fields,
        ...(binding.field ? [binding.field] : []),
      ]);
      const missing = [...requiredFields].filter(
        field => !declaredFields.has(field),
      );
      return missing.length > 0
        ? [{
            level: 'error' as const,
            path: `bindings.${dataView.binding}`,
            message: `场景绑定缺少映射字段：${missing.join('、')}`,
          }]
        : [];
    });
    const semanticIssues: Array<{
      level: 'error';
      path: string;
      message: string;
    }> = [];
    const landmarkIds = new Set(scene.viewport.landmarks.map(item => item.id));
    for (const [index, camera] of scene.viewport.cameras.entries()) {
      if (camera.landmarkId && !landmarkIds.has(camera.landmarkId)) {
        semanticIssues.push({
          level: 'error',
          path: `config.viewport.cameras.${index}.landmarkId`,
          message: `相机地标不存在：${camera.landmarkId}`,
        });
      }
    }
    const dataViewFields = new Map(scene.dataViews.map(dataView => [
      dataView.id,
      new Set([
        ...dataView.select.map(field => dataView.rename[field] ?? field),
        ...dataView.lookup.flatMap(lookup =>
          lookup.fields.map(field => `${lookup.prefix}${field}`),
        ),
      ]),
    ]));
    for (const [index, construct] of scene.constructs.entries()) {
      const dataViewId = construct.params.dataViewId;
      if (typeof dataViewId !== 'string' || !dataViewId)
        continue;
      const fields = dataViewFields.get(dataViewId);
      if (!fields) {
        semanticIssues.push({
          level: 'error',
          path: `config.constructs.${index}.params.dataViewId`,
          message: `Scene construct 引用了未知 DataView：${dataViewId}`,
        });
        continue;
      }
      for (const fieldName of ['keyField', 'labelField'] as const) {
        const field = construct.params[fieldName];
        if (
          typeof field === 'string'
          && field
          && field !== '$rowId'
          && !fields.has(field)
        ) {
          semanticIssues.push({
            level: 'error',
            path: `config.constructs.${index}.params.${fieldName}`,
            message: `视觉映射字段不存在：${field}`,
          });
        }
      }
    }
    return [...bindingIssues, ...semanticIssues];
  },
  loader: () => import('./SceneBlock.vue').then(module => module.default),
};
